import React, { useEffect, useMemo, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  Save,
  Trash2,
  Plus,
  Upload,
  FileText,
  ExternalLink,
} from "lucide-react";

const FREQ_OPTIONS = ["annual", "quarterly", "monthly", "one-off"];
const empty = {
  name: "",
  supplier_id: "",
  machine_ids: [],
  period_start: "",
  period_end: "",
  cost: "",
  payment_frequency: "annual",
  auto_renew: false,
  inclusions: [],
  document_paths: [],
  notes: "",
};

/**
 * Manager-only — create or edit a Maintenance / Service Agreement.
 *
 * One agreement can cover multiple machines (e.g. an Evok3D/HP contract
 * that spans printers + build units + processing stations), so machines
 * are a multi-select rather than single field. Supplier is optional but
 * strongly encouraged so the agreement auto-links into the Supplier 360°
 * view. Documents (PDF or images) upload via the existing /upload endpoint.
 */
const ServiceAgreementFormDialog = ({
  open,
  initial,
  suppliers,
  machines,
  onClose,
  onSaved,
  onDelete,
}) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [incDraft, setIncDraft] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              ...empty,
              ...initial,
              supplier_id: initial.supplier_id || "",
              machine_ids: initial.machine_ids || [],
              inclusions: initial.inclusions || [],
              document_paths: initial.document_paths || [],
              cost: initial.cost == null ? "" : String(initial.cost),
              period_start: (initial.period_start || "").slice(0, 10),
              period_end: (initial.period_end || "").slice(0, 10),
            }
          : empty,
      );
      setIncDraft("");
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMachine = (mid) =>
    setForm((f) => ({
      ...f,
      machine_ids: f.machine_ids.includes(mid)
        ? f.machine_ids.filter((x) => x !== mid)
        : [...f.machine_ids, mid],
    }));

  const addInc = () => {
    const s = incDraft.trim();
    if (!s) return;
    if (form.inclusions.some((x) => x.toLowerCase() === s.toLowerCase())) {
      setIncDraft("");
      return;
    }
    set("inclusions", [...form.inclusions, s]);
    setIncDraft("");
  };

  const uploadDoc = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("document_paths", [...form.document_paths, data.path]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.period_end) return toast.error("End date required for renewal alerts");
    setBusy(true);
    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id || null,
        cost: form.cost === "" ? null : Number(form.cost),
      };
      if (initial?.id) {
        const { data } = await api.put(`/service-agreements/${initial.id}`, payload);
        toast.success("Agreement updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/service-agreements", payload);
        toast.success(`Added ${data.name}`);
        onSaved?.(data);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Deactivate "${form.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/service-agreements/${initial.id}`);
      toast.success("Deactivated");
      onDelete?.(initial.id);
      onClose();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const supplierMachines = useMemo(() => {
    // Hint: if supplier is chosen, sort machines with matching supplier
    // (via linked techs) to the top so the manager can bulk-tick them fast.
    // Falls back to all machines otherwise.
    if (!form.supplier_id) return machines;
    const preferred = machines.filter((m) =>
      (m.tech_links || []).some((tl) =>
        (tl.supplier_name || "").toLowerCase() ===
        (suppliers.find((s) => s.id === form.supplier_id)?.name || "").toLowerCase(),
      ),
    );
    const others = machines.filter((m) => !preferred.includes(m));
    return [...preferred, ...others];
  }, [form.supplier_id, machines, suppliers]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="agreement-form-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card bg-white max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-2xl">
            {initial ? "Edit agreement" : "New service agreement"}
          </h2>
          <button
            data-testid="agreement-form-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Agreement name *</div>
            <input
              data-testid="agreement-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Evok3D HP Printer Agreement 2026"
              autoFocus
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Supplier</div>
            <select
              data-testid="agreement-supplier-select"
              className="nb-input"
              value={form.supplier_id}
              onChange={(e) => set("supplier_id", e.target.value)}
            >
              <option value="">— None —</option>
              {(suppliers || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Payment frequency</div>
            <select
              data-testid="agreement-freq-select"
              className="nb-input"
              value={form.payment_frequency}
              onChange={(e) => set("payment_frequency", e.target.value)}
            >
              {FREQ_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Period start</div>
            <input
              data-testid="agreement-period-start"
              type="date"
              className="nb-input"
              value={form.period_start}
              onChange={(e) => set("period_start", e.target.value)}
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Period end *</div>
            <input
              data-testid="agreement-period-end"
              type="date"
              className="nb-input"
              value={form.period_end}
              onChange={(e) => set("period_end", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Cost</div>
            <input
              data-testid="agreement-cost"
              type="number"
              step="0.01"
              className="nb-input"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              placeholder="0.00"
            />
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              data-testid="agreement-auto-renew"
              checked={form.auto_renew}
              onChange={(e) => set("auto_renew", e.target.checked)}
            />
            <span className="text-sm font-bold">Auto-renew</span>
          </label>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">
              Machines covered ({form.machine_ids.length})
            </div>
            <div
              className="max-h-40 overflow-y-auto border-2 border-black rounded-lg p-2 flex flex-col gap-1 bg-white"
              data-testid="agreement-machine-list"
            >
              {supplierMachines.length === 0 && (
                <div className="text-xs text-[#607087] p-2">
                  No machines yet — add some on the Machinery page first.
                </div>
              )}
              {supplierMachines.map((m) => (
                <label
                  key={m.id}
                  className="flex items-center gap-2 text-sm px-1 py-0.5 hover:bg-[#F5F7FA] rounded"
                >
                  <input
                    type="checkbox"
                    data-testid={`agreement-machine-${m.id}`}
                    checked={form.machine_ids.includes(m.id)}
                    onChange={() => toggleMachine(m.id)}
                  />
                  <span className="flex-1">{m.name}</span>
                  {m.category && (
                    <span className="text-[10px] text-[#607087]">{m.category}</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Main service inclusions</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.inclusions.map((s) => (
                <span
                  key={s}
                  data-testid={`agreement-inc-chip-${s}`}
                  className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 bg-[#EAF5FB] text-xs font-bold"
                >
                  {s}
                  <button
                    onClick={() =>
                      set(
                        "inclusions",
                        form.inclusions.filter((x) => x !== s),
                      )
                    }
                    className="p-0.5 hover:bg-white rounded-full"
                    aria-label={`remove ${s}`}
                  >
                    <X size={10} strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="agreement-inc-input"
                className="nb-input flex-1"
                value={incDraft}
                onChange={(e) => setIncDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addInc();
                  }
                }}
                placeholder="e.g. Annual calibration · 2 free call-outs · Parts 15% off"
              />
              <button
                data-testid="agreement-inc-add-btn"
                onClick={addInc}
                type="button"
                className="nb-btn bg-white px-3"
              >
                <Plus size={14} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Agreement documents</div>
            <div className="flex flex-col gap-1 mb-2">
              {form.document_paths.map((p, i) => (
                <div
                  key={p}
                  data-testid={`agreement-doc-${i}`}
                  className="flex items-center gap-2 text-sm border-2 border-black rounded-lg px-2 py-1.5 bg-white"
                >
                  <FileText size={14} strokeWidth={2.5} />
                  <a
                    href={imageUrl(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 truncate hover:underline text-[#228EBF] font-bold"
                  >
                    {p.split("/").pop()}
                  </a>
                  <a
                    href={imageUrl(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nb-btn bg-white p-1"
                    title="Open in new tab"
                  >
                    <ExternalLink size={12} strokeWidth={2.5} />
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      set(
                        "document_paths",
                        form.document_paths.filter((_, j) => j !== i),
                      )
                    }
                    className="nb-btn bg-white p-1"
                    aria-label="remove"
                  >
                    <Trash2 size={12} strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
            <label
              className={`nb-btn bg-white px-3 py-2 flex items-center gap-2 text-sm cursor-pointer w-max ${
                uploading ? "opacity-50 pointer-events-none" : ""
              }`}
              data-testid="agreement-upload-btn"
            >
              <Upload size={14} strokeWidth={2.5} />
              {uploading ? "Uploading…" : "Upload PDF or photo"}
              <input
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadDoc(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="agreement-notes"
              className="nb-input min-h-[70px]"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Cancellation window, escalation contact, etc."
            />
          </label>
        </div>

        <div className="p-5 border-t-2 border-black flex items-center justify-between gap-2 flex-wrap">
          {initial?.id ? (
            <button
              data-testid="agreement-delete-btn"
              onClick={del}
              disabled={busy}
              className="nb-btn bg-white px-3 py-2 flex items-center gap-2 text-sm text-[#8A1F1F]"
            >
              <Trash2 size={14} strokeWidth={2.5} /> Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid="agreement-cancel-btn"
              onClick={onClose}
              className="nb-btn bg-white px-4 py-2"
            >
              Cancel
            </button>
            <button
              data-testid="agreement-save-btn"
              onClick={save}
              disabled={busy}
              className="nb-btn nb-btn-primary px-5 py-2 flex items-center gap-2"
            >
              <Save size={14} strokeWidth={2.5} /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceAgreementFormDialog;
